import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  CurrencyDollar,
  Warning,
  ShieldWarning,
  Truck,
  HardHat,
  Lightning,
  CaretRight,
  ArrowsClockwise,
  CheckCircle,
  Clock,
  Prohibit,
  ChartLineUp,
  DownloadSimple,
} from '@phosphor-icons/react';
import { ObserverInsights } from '../components/ObserverInsights';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function fmt(val) {
  if (val === null || val === undefined) return '0';
  if (val >= 10000000) return `${(val / 10000000).toFixed(2)} Cr`;
  if (val >= 100000) return `${(val / 100000).toFixed(2)} L`;
  if (val >= 1000) return `${(val / 1000).toFixed(1)}K`;
  return val.toLocaleString('en-IN');
}

const STATUS_COLORS = {
  green: { bg: 'bg-emerald-50', border: 'border-emerald-200', dot: 'bg-emerald-500', text: 'text-emerald-700' },
  amber: { bg: 'bg-amber-50', border: 'border-amber-200', dot: 'bg-amber-500', text: 'text-amber-700' },
  red: { bg: 'bg-red-50', border: 'border-red-200', dot: 'bg-red-500', text: 'text-red-700' },
};

function StatusDot({ status }) {
  const c = STATUS_COLORS[status] || STATUS_COLORS.green;
  return <div className={`w-2.5 h-2.5 rounded-full ${c.dot}`} />;
}

function MetricCard({ label, value, sub, status, onClick }) {
  const c = STATUS_COLORS[status] || STATUS_COLORS.green;
  return (
    <div
      onClick={onClick}
      className={`${c.bg} ${c.border} border rounded-lg px-4 py-3 ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
      data-testid={`metric-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="text-[11px] text-zinc-500 font-medium uppercase tracking-wide">{label}</div>
      <div className={`text-xl font-bold mt-0.5 ${c.text}`}>{value}</div>
      {sub && <div className="text-[11px] text-zinc-400 mt-0.5">{sub}</div>}
    </div>
  );
}

function SectionHeader({ icon: Icon, title, status, iconColor }) {
  const c = STATUS_COLORS[status] || STATUS_COLORS.green;
  return (
    <div className="flex items-center gap-2 mb-3">
      <Icon size={18} weight="fill" className={iconColor || c.text} />
      <span className="text-sm font-bold text-zinc-800">{title}</span>
      <StatusDot status={status} />
    </div>
  );
}

export const FounderDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const navigate = useNavigate();

  const fetchDashboard = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/dashboard/founder`, { credentials: 'include' });
      if (res.ok) setData(await res.json());
    } catch (e) { /* silent */ }
    setLoading(false);
    setRefreshing(false);
  }, []);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <ArrowsClockwise size={24} className="animate-spin text-zinc-400" />
      </div>
    );
  }

  if (!data) {
    return <div className="p-8 text-center text-zinc-500">Failed to load dashboard</div>;
  }

  const { financial_command: fin, project_risk: pr, commercial_risk: cr, procurement_control: pc, execution_health: eh, action_snapshot: act } = data;

  return (
    <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-5" data-testid="founder-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-lg font-bold text-zinc-900">Command Center</h1>
          <p className="text-xs text-zinc-400 mt-0.5">Business health at a glance</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={async () => {
              try {
                const res = await axios.get(`${BACKEND_URL}/api/dashboard/export?format=csv`, { withCredentials: true, responseType: 'blob' });
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const a = document.createElement('a'); a.href = url; a.download = 'dashboard_export.csv';
                document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
                toast.success('Dashboard exported');
              } catch { toast.error('Export failed'); }
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100"
            data-testid="dashboard-export"
          >
            <DownloadSimple size={14} /> Export CSV
          </button>
          <button
            onClick={() => fetchDashboard(true)}
            disabled={refreshing}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-zinc-600 bg-white border border-zinc-200 rounded-md hover:bg-zinc-50 disabled:opacity-50"
            data-testid="dashboard-refresh"
          >
            <ArrowsClockwise size={14} className={refreshing ? 'animate-spin' : ''} />
            {refreshing ? 'Refreshing' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Observer Insights Panel */}
      <ObserverInsights />

      {/* Demand Health (Cross-Project) */}
      <DemandHealthCard navigate={navigate} />

      {/* Recipe Coverage KPI */}
      <RecipeCoverageCard navigate={navigate} />

      {/* Daily Control + Custom Items + Adoption */}
      <DailyControlCard navigate={navigate} />

      {/* Row 1: Financial + Commercial */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        {/* A. Financial Command Center */}
        <div className={`${STATUS_COLORS[fin.status].bg} ${STATUS_COLORS[fin.status].border} border rounded-xl p-5`} data-testid="financial-command">
          <SectionHeader icon={CurrencyDollar} title="Financial Command" status={fin.status} iconColor="text-emerald-600" />
          <div className="grid grid-cols-2 gap-3">
            <MetricCard
              label="Total Receivable"
              value={`₹${fmt(fin.total_receivable)}`}
              status={fin.total_receivable > 0 ? 'amber' : 'green'}
            />
            <MetricCard
              label="Overdue Invoices"
              value={`₹${fmt(fin.overdue_amount)}`}
              sub={`${fin.overdue_count} invoice(s)`}
              status={fin.overdue_count > 0 ? 'red' : 'green'}
              onClick={fin.overdue_count > 0 ? () => navigate('/dashboard') : undefined}
            />
            <MetricCard
              label="Collection (30d)"
              value={`${fin.collection_pct_30d}%`}
              status={fin.collection_pct_30d >= 80 ? 'green' : fin.collection_pct_30d >= 50 ? 'amber' : 'red'}
            />
            <MetricCard
              label="Invoiced vs Received"
              value={`₹${fmt(fin.invoiced_vs_received_gap)}`}
              sub={`₹${fmt(fin.total_invoiced)} inv / ₹${fmt(fin.total_received)} recv`}
              status={fin.invoiced_vs_received_gap > 100000 ? 'amber' : 'green'}
            />
          </div>
          {/* CN/DN Summary */}
          {(fin.total_credit_notes > 0 || fin.total_debit_notes > 0) && (
            <div className="mt-3 pt-3 border-t border-zinc-200/60">
              <div className="flex items-center gap-3 text-xs cursor-pointer hover:opacity-80" onClick={() => navigate('/adjustments')} data-testid="fin-cndn-summary">
                <span className="text-zinc-500 font-semibold">CN/DN:</span>
                <span className="text-red-600 font-bold">-₹{fmt(fin.total_credit_notes || 0)}</span>
                <span className="text-green-600 font-bold">+₹{fmt(fin.total_debit_notes || 0)}</span>
                <span className={`font-bold ${(fin.net_cn_dn_adjustment || 0) >= 0 ? 'text-green-700' : 'text-red-700'}`}>Net: ₹{fmt(fin.net_cn_dn_adjustment || 0)}</span>
              </div>
            </div>
          )}
        </div>

        {/* C. Commercial Risk Summary */}
        <div className={`${STATUS_COLORS[cr.status].bg} ${STATUS_COLORS[cr.status].border} border rounded-xl p-5`} data-testid="commercial-risk">
          <SectionHeader icon={ShieldWarning} title="Commercial Risk" status={cr.status} iconColor="text-red-500" />
          <div className="grid grid-cols-2 gap-3">
            <MetricCard
              label="VO Not Billed"
              value={`₹${fmt(cr.vo_not_billed)}`}
              status={cr.vo_not_billed > 0 ? 'red' : 'green'}
            />
            <MetricCard
              label="Bills Not Invoiced"
              value={`₹${fmt(cr.bills_not_invoiced)}`}
              status={cr.bills_not_invoiced > 0 ? 'amber' : 'green'}
            />
            <MetricCard
              label="Not Collected"
              value={`₹${fmt(cr.invoices_not_collected)}`}
              status={cr.invoices_not_collected > 0 ? 'amber' : 'green'}
            />
            <MetricCard
              label="Overrides"
              value={cr.override_count}
              sub="Enforcement bypasses"
              status={cr.override_count > 0 ? 'red' : 'green'}
              onClick={() => navigate('/audit')}
            />
          </div>
        </div>
      </div>

      {/* Row 2: Project Risk + Procurement + Execution */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        {/* B. Project Risk Panel */}
        <div className="bg-white border border-zinc-200 rounded-xl p-5" data-testid="project-risk">
          <SectionHeader icon={Warning} title="Project Risk" status={pr.financial_exposure_count > 0 ? 'red' : pr.vo_pending_count > 0 ? 'amber' : 'green'} iconColor="text-amber-500" />
          <div className="space-y-2">
            <div className="flex items-center justify-between px-3 py-2 bg-zinc-50 rounded-md">
              <span className="text-xs text-zinc-600">Active Projects</span>
              <span className="text-sm font-bold text-zinc-800">{pr.total_active_projects}</span>
            </div>
            <div className="flex items-center justify-between px-3 py-2 bg-red-50 rounded-md cursor-pointer hover:bg-red-100" onClick={() => navigate('/dashboard')}>
              <span className="text-xs text-red-700 flex items-center gap-1"><Prohibit size={12} /> Blocked</span>
              <span className="text-sm font-bold text-red-700">{pr.blocked_count}</span>
            </div>
            <div className="flex items-center justify-between px-3 py-2 bg-amber-50 rounded-md">
              <span className="text-xs text-amber-700 flex items-center gap-1"><Clock size={12} /> VO Pending Approval</span>
              <span className="text-sm font-bold text-amber-700">{pr.vo_pending_count}</span>
            </div>
            <div className="flex items-center justify-between px-3 py-2 bg-red-50 rounded-md">
              <span className="text-xs text-red-700 flex items-center gap-1"><ShieldWarning size={12} /> Money at Risk</span>
              <span className="text-sm font-bold text-red-700">{pr.financial_exposure_count}</span>
            </div>
          </div>
          {pr.top_risk_projects.length > 0 && (
            <div className="mt-3 pt-3 border-t border-zinc-100">
              <div className="text-[10px] uppercase text-zinc-400 font-semibold mb-1.5">Top Risk Projects</div>
              {pr.top_risk_projects.map((rp, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between py-1.5 cursor-pointer hover:bg-zinc-50 px-2 rounded"
                  onClick={() => navigate(`/projects/${rp.project_id}`)}
                  data-testid={`risk-project-${i}`}
                >
                  <span className="text-xs text-zinc-700 font-medium truncate max-w-[140px]">{rp.name}</span>
                  <div className="flex items-center gap-1 flex-wrap justify-end">
                    {rp.issues.map((issue, j) => (
                      <span key={j} className="text-[9px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 whitespace-nowrap">{issue}</span>
                    ))}
                    <CaretRight size={10} className="text-zinc-400" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* D. Procurement & Vendor Control */}
        <div className={`${STATUS_COLORS[pc.status].bg} ${STATUS_COLORS[pc.status].border} border rounded-xl p-5`} data-testid="procurement-control">
          <SectionHeader icon={Truck} title="Procurement" status={pc.status} iconColor="text-blue-500" />
          <div className="space-y-2">
            <div className="flex items-center justify-between px-3 py-2.5 bg-white/60 rounded-md cursor-pointer hover:bg-white" onClick={() => navigate('/procurement/reconciliation?tab=grn')}>
              <span className="text-xs text-zinc-600">GRN Mismatches</span>
              <span className={`text-sm font-bold ${pc.grn_mismatch_count > 0 ? 'text-red-600' : 'text-green-600'}`}>{pc.grn_mismatch_count}</span>
            </div>
            <div className="flex items-center justify-between px-3 py-2.5 bg-white/60 rounded-md cursor-pointer hover:bg-white" onClick={() => navigate('/procurement/reconciliation?tab=indent')}>
              <span className="text-xs text-zinc-600">Pending Indents</span>
              <span className={`text-sm font-bold ${pc.pending_indent_count > 0 ? 'text-amber-600' : 'text-green-600'}`}>{pc.pending_indent_count}</span>
            </div>
            <div className="flex items-center justify-between px-3 py-2.5 bg-white/60 rounded-md cursor-pointer hover:bg-white" onClick={() => navigate('/procurement/reconciliation?tab=payment')}>
              <span className="text-xs text-zinc-600">Overpayments</span>
              <span className={`text-sm font-bold ${(pc.overpayment_count || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>{pc.overpayment_count || 0}</span>
            </div>
            <div className="flex items-center justify-between px-3 py-2.5 bg-white/60 rounded-md cursor-pointer hover:bg-white" onClick={() => navigate('/procurement/reconciliation?tab=unmapped')}>
              <span className="text-xs text-zinc-600">Unmapped Materials</span>
              <span className={`text-sm font-bold ${(pc.unmapped_materials_count || 0) > 0 ? 'text-amber-600' : 'text-green-600'}`}>{pc.unmapped_materials_count || 0}</span>
            </div>
            <div className="flex items-center justify-between px-3 py-2.5 bg-white/60 rounded-md cursor-pointer hover:bg-white" onClick={() => navigate('/procurement/reconciliation?tab=payment')}>
              <span className="text-xs text-zinc-600">Advance Mismatch</span>
              <span className={`text-sm font-bold ${pc.advance_mismatch_count > 0 ? 'text-red-600' : 'text-green-600'}`}>{pc.advance_mismatch_count}</span>
            </div>
          </div>
        </div>

        {/* E. Execution Health */}
        <div className={`${STATUS_COLORS[eh.status].bg} ${STATUS_COLORS[eh.status].border} border rounded-xl p-5`} data-testid="execution-health">
          <SectionHeader icon={HardHat} title="Execution Health" status={eh.status} iconColor="text-violet-500" />
          <div className="space-y-2">
            <div className="flex items-center justify-between px-3 py-2.5 bg-white/60 rounded-md">
              <span className="text-xs text-zinc-600 flex items-center gap-1"><CheckCircle size={12} className="text-green-500" /> On Track</span>
              <span className="text-sm font-bold text-green-600">{eh.on_track_count}</span>
            </div>
            <div className="flex items-center justify-between px-3 py-2.5 bg-white/60 rounded-md">
              <span className="text-xs text-zinc-600 flex items-center gap-1"><Warning size={12} className="text-amber-500" /> At Risk</span>
              <span className={`text-sm font-bold ${eh.at_risk_count > 0 ? 'text-red-600' : 'text-green-600'}`}>{eh.at_risk_count}</span>
            </div>
            <div className="flex items-center justify-between px-3 py-2.5 bg-white/60 rounded-md">
              <span className="text-xs text-zinc-600">DPR Missing Today</span>
              <span className={`text-sm font-bold ${eh.dpr_missing_today > 0 ? 'text-red-600' : 'text-green-600'}`}>{eh.dpr_missing_today}</span>
            </div>
            <div className="flex items-center justify-between px-3 py-2.5 bg-white/60 rounded-md">
              <span className="text-xs text-zinc-600">Manpower Deviation</span>
              <span className={`text-sm font-bold ${eh.manpower_deviation_count > 0 ? 'text-amber-600' : 'text-green-600'}`}>{eh.manpower_deviation_count}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Row 3: Action Snapshot */}
      <div className="bg-white border border-zinc-200 rounded-xl p-5" data-testid="action-snapshot">
        <SectionHeader icon={Lightning} title="Action Snapshot" status={act.pending_approvals > 0 || act.escalations_triggered > 0 ? 'amber' : 'green'} iconColor="text-amber-500" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Critical Tasks */}
          <div className="lg:col-span-2">
            <div className="text-[10px] uppercase text-zinc-400 font-semibold mb-2">Top Critical Tasks</div>
            {act.critical_tasks.length === 0 ? (
              <div className="text-xs text-zinc-400 py-3">No critical tasks pending</div>
            ) : (
              <div className="space-y-1.5">
                {act.critical_tasks.map((t, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 px-3 py-2 bg-zinc-50 rounded-md cursor-pointer hover:bg-zinc-100 transition-colors"
                    onClick={() => navigate('/action-center')}
                    data-testid={`action-task-${i}`}
                  >
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold ${t.priority === 'Critical' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                      {t.priority}
                    </span>
                    <span className="text-xs text-zinc-700 flex-1 truncate">{t.title}</span>
                    <span className="text-[10px] text-zinc-400">{t.task_id}</span>
                    <CaretRight size={12} className="text-zinc-400" />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Approvals + Escalations */}
          <div className="space-y-3">
            <div
              className="px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg cursor-pointer hover:bg-amber-100"
              onClick={() => navigate('/action-center')}
              data-testid="pending-approvals-card"
            >
              <div className="text-[10px] text-amber-600 uppercase font-semibold">Pending Approvals</div>
              <div className="text-2xl font-bold text-amber-700 mt-0.5">{act.pending_approvals}</div>
              <div className="text-[10px] text-amber-500 mt-0.5">VOs + Bills + Indents</div>
            </div>
            <div
              className={`px-4 py-3 rounded-lg border cursor-pointer ${act.escalations_triggered > 0 ? 'bg-red-50 border-red-200 hover:bg-red-100' : 'bg-zinc-50 border-zinc-200 hover:bg-zinc-100'}`}
              data-testid="escalations-card"
            >
              <div className={`text-[10px] uppercase font-semibold ${act.escalations_triggered > 0 ? 'text-red-600' : 'text-zinc-500'}`}>Escalated Alerts</div>
              <div className={`text-2xl font-bold mt-0.5 ${act.escalations_triggered > 0 ? 'text-red-700' : 'text-zinc-600'}`}>{act.escalations_triggered}</div>
              <div className={`text-[10px] mt-0.5 ${act.escalations_triggered > 0 ? 'text-red-500' : 'text-zinc-400'}`}>Overdue items pushed up</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ========== DEMAND HEALTH CARD (Cross-Project) ==========
const DemandHealthCard = ({ navigate }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/reports/demand-summary`, { withCredentials: true });
        setData(res.data);
      } catch { /* ignore */ }
      setLoading(false);
    };
    fetch();
  }, []);

  if (loading || !data) return null;

  const { summary, projects } = data;
  const flaggedProjects = projects.filter(p => p.flag_count > 0);

  if (flaggedProjects.length === 0 && summary.total_projects === 0) return null;

  const severityColor = summary.projects_with_flags > summary.total_projects * 0.5 ? 'red' :
    summary.projects_with_flags > 0 ? 'amber' : 'green';
  const sc = STATUS_COLORS[severityColor];

  return (
    <div className={`${sc.bg} ${sc.border} border rounded-xl p-5 mb-4`} data-testid="demand-health-card">
      <SectionHeader icon={Lightning} title="Demand Health" status={severityColor} iconColor="text-blue-600" />
      <div className="grid grid-cols-4 gap-3 mb-3">
        <MetricCard label="Active Projects" value={summary.total_projects} status="green" />
        <MetricCard label="With Flags" value={summary.projects_with_flags} status={summary.projects_with_flags > 0 ? 'amber' : 'green'} />
        <MetricCard label="Material Gaps" value={summary.total_material_gaps} status={summary.total_material_gaps > 0 ? 'red' : 'green'} />
        <MetricCard label="MP Gap (days)" value={summary.total_manpower_gap_days} status={summary.total_manpower_gap_days > 0 ? 'amber' : 'green'} />
      </div>
      {flaggedProjects.length > 0 && (
        <div className="space-y-1.5">
          {flaggedProjects.slice(0, 5).map((p, i) => (
            <div key={i} className="flex items-center justify-between bg-white border border-zinc-200 rounded px-3 py-2 cursor-pointer hover:bg-zinc-50"
              onClick={() => navigate(`/projects/${p.project_id}`)} data-testid={`demand-flag-${i}`}>
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-zinc-800">{p.project_name}</span>
                <span className="text-[9px] text-zinc-400">{p.status}</span>
              </div>
              <div className="flex gap-1.5">
                {p.flags.map((f, fi) => (
                  <span key={fi} className={`text-[8px] font-bold px-1.5 py-0.5 rounded ${
                    f.severity === 'critical' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                  }`}>
                    {f.type.replace(/_/g, ' ')}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


const RecipeCoverageCard = ({ navigate }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/master/recipe-coverage`, { withCredentials: true });
        setData(res.data);
      } catch { /* ignore */ }
    };
    fetch();
  }, []);

  if (!data || data.total === 0) return null;
  const pct = data.coverage_pct;
  const status = pct >= 80 ? 'green' : pct >= 50 ? 'amber' : 'red';
  const sc = STATUS_COLORS[status];

  return (
    <div className={`${sc.bg} ${sc.border} border rounded-xl p-5 mb-4`} data-testid="recipe-coverage-card">
      <SectionHeader icon={ChartLineUp} title="Recipe Coverage" status={status} iconColor="text-violet-600" />
      <div className="flex items-center gap-4">
        <div className="flex-1">
          <div className="flex items-end gap-2">
            <span className={`text-3xl font-bold ${sc.text}`} data-testid="recipe-coverage-pct">{pct}%</span>
            <span className="text-xs text-zinc-500 mb-1">of master items have complete recipes</span>
          </div>
          <div className="h-2 w-full bg-zinc-100 rounded overflow-hidden mt-2">
            <div className={`h-full ${status === 'green' ? 'bg-emerald-500' : status === 'amber' ? 'bg-amber-500' : 'bg-red-500'} transition-all`} style={{ width: `${Math.max(2, pct)}%` }} />
          </div>
          <div className="text-[11px] text-zinc-500 mt-1.5">
            <strong className="text-zinc-700">{data.complete}</strong> complete · <strong className="text-zinc-700">{data.incomplete}</strong> incomplete of {data.total} items
            <span className="ml-2 text-zinc-400">(material + manpower both present = complete)</span>
          </div>
        </div>
        {data.incomplete > 0 && (
          <button onClick={() => navigate('/master-studio')}
            className="flex items-center gap-1 text-xs font-semibold text-violet-700 bg-violet-50 border border-violet-200 px-3 py-2 rounded hover:bg-violet-100"
            data-testid="recipe-coverage-backfill-link">
            Backfill {data.incomplete} missing <CaretRight size={12} />
          </button>
        )}
      </div>
    </div>
  );
};



// ========== DAILY CONTROL + CUSTOM ITEMS + ADOPTION ==========
const DailyControlCard = ({ navigate }) => {
  const [control, setControl] = useState(null);
  const [customs, setCustoms] = useState(null);
  const [adoption, setAdoption] = useState(null);
  const [promoting, setPromoting] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const [cRes, cuRes, aRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/reports/daily-control`, { withCredentials: true }),
          axios.get(`${BACKEND_URL}/api/reports/custom-items`, { withCredentials: true }),
          axios.get(`${BACKEND_URL}/api/reports/action-adoption`, { withCredentials: true }),
        ]);
        setControl(cRes.data);
        setCustoms(cuRes.data);
        setAdoption(aRes.data);
      } catch { }
    };
    fetch();
  }, []);

  const promoteItem = async (desc, unit, trade) => {
    setPromoting(desc);
    try {
      await axios.post(`${BACKEND_URL}/api/reports/custom-items/promote`, { description: desc, unit, trade_name: trade }, { withCredentials: true });
      toast.success(`Promoted "${desc}" to master`);
      // Refresh
      const res = await axios.get(`${BACKEND_URL}/api/reports/custom-items`, { withCredentials: true });
      setCustoms(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to promote'); }
    setPromoting(null);
  };

  if (!control) return null;
  const s = control.summary;

  return (
    <div className="space-y-4 mb-4" data-testid="daily-control-section">
      {/* Daily Control */}
      <div className="bg-white border border-zinc-200 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <Lightning size={18} className="text-blue-600" />
          <h3 className="text-sm font-bold text-zinc-900">Daily Control</h3>
          <div className="flex gap-2 ml-auto">
            <span className="text-[9px] font-bold px-2 py-0.5 rounded bg-red-100 text-red-700">{s.at_risk_count} at risk</span>
            <span className="text-[9px] font-bold px-2 py-0.5 rounded bg-amber-100 text-amber-700">{s.pending_count} pending</span>
            <span className="text-[9px] font-bold px-2 py-0.5 rounded bg-emerald-100 text-emerald-700">{s.ready_count} ready</span>
          </div>
        </div>
        {/* Top Attention Items */}
        {control.top_attention?.length > 0 && (
          <div className="space-y-1.5 mb-3">
            <p className="text-[9px] text-zinc-500 font-bold uppercase tracking-wider">Top Attention Items</p>
            {control.top_attention.map((a, i) => (
              <div key={i} className="flex items-center justify-between bg-zinc-50 border border-zinc-100 px-3 py-2 rounded cursor-pointer hover:bg-zinc-100"
                onClick={() => navigate(`/projects/${a.project_id}`)} data-testid={`attention-item-${i}`}>
                <div className="flex items-center gap-2">
                  <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded ${a.type === 'material_gap' ? 'bg-red-100 text-red-700' : a.type === 'low_coverage' ? 'bg-amber-100 text-amber-700' : a.type === 'high_override' ? 'bg-violet-100 text-violet-700' : 'bg-zinc-200 text-zinc-600'}`}>
                    {a.type.replace(/_/g, ' ')}
                  </span>
                  <span className="text-xs text-zinc-800 font-medium">{a.project}</span>
                </div>
                <span className="text-[10px] text-zinc-500">{a.detail}</span>
              </div>
            ))}
          </div>
        )}
        {/* Project Categories */}
        <div className="grid grid-cols-3 gap-3">
          {[['At Risk', control.at_risk, 'red'], ['Pending Actions', control.pending_actions, 'amber'], ['Ready', control.ready, 'green']].map(([label, items, color]) => (
            <div key={label} className={`border border-${color === 'red' ? 'red' : color === 'amber' ? 'amber' : 'emerald'}-200 p-2.5 rounded`}>
              <p className={`text-[9px] font-bold uppercase text-${color === 'red' ? 'red' : color === 'amber' ? 'amber' : 'emerald'}-600 mb-1.5`}>{label} ({items?.length || 0})</p>
              {(items || []).slice(0, 3).map((p, i) => (
                <div key={i} className="text-[10px] text-zinc-700 py-0.5 cursor-pointer hover:text-blue-700" onClick={() => navigate(`/projects/${p.project_id}`)}>
                  {p.project_name}
                  {p.draft_indents > 0 && <span className="ml-1 text-amber-600">{p.draft_indents} indents</span>}
                </div>
              ))}
              {(items?.length || 0) > 3 && <p className="text-[9px] text-zinc-400 mt-0.5">+{items.length - 3} more</p>}
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* Custom Item Tracking */}
        {customs && customs.total_custom_items > 0 && (
          <div className="bg-white border border-zinc-200 rounded-xl p-5" data-testid="custom-items-card">
            <h3 className="text-sm font-bold text-zinc-900 mb-2">Custom Items</h3>
            <div className="flex gap-3 mb-3">
              <div><span className="text-lg font-bold text-zinc-800">{customs.total_custom_items}</span><span className="text-[10px] text-zinc-500 ml-1">total</span></div>
              <div><span className="text-lg font-bold text-amber-700">{customs.repeated_items}</span><span className="text-[10px] text-zinc-500 ml-1">repeated</span></div>
            </div>
            {customs.promotion_candidates?.length > 0 && (
              <div className="space-y-1">
                <p className="text-[9px] text-zinc-500 font-bold uppercase">Promote to Master</p>
                {customs.promotion_candidates.slice(0, 5).map((c, i) => (
                  <div key={i} className="flex items-center justify-between text-[10px] bg-zinc-50 border border-zinc-100 px-2.5 py-1.5 rounded">
                    <div><span className="text-zinc-800 font-medium">{c.description}</span><span className="text-zinc-400 ml-1.5">x{c.occurrence_count} in {c.project_count} project{c.project_count > 1 ? 's' : ''}</span></div>
                    <button onClick={() => promoteItem(c.description, c.unit, c.trade_name)} disabled={promoting === c.description}
                      className="text-blue-600 hover:text-blue-800 font-bold text-[9px] px-2 py-0.5 border border-blue-300 rounded disabled:opacity-50" data-testid={`promote-${i}`}>
                      {promoting === c.description ? '...' : 'Promote'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Action Adoption */}
        {adoption && (
          <div className="bg-white border border-zinc-200 rounded-xl p-5" data-testid="action-adoption-card">
            <h3 className="text-sm font-bold text-zinc-900 mb-3">Action Adoption</h3>
            <div className="space-y-3">
              <div>
                <p className="text-[9px] text-zinc-500 font-bold uppercase mb-1">Draft Indents (from demand)</p>
                <div className="flex items-center gap-3">
                  <span className="text-lg font-bold text-zinc-800">{adoption.indents.total_demand_indents}</span>
                  <div className="flex-1 h-2 bg-zinc-100 rounded-full overflow-hidden"><div className="h-full bg-blue-500 rounded-full" style={{ width: `${adoption.indents.po_conversion_pct}%` }} /></div>
                  <span className="text-[10px] font-bold text-zinc-600">{adoption.indents.po_conversion_pct}% to PO</span>
                </div>
              </div>
              <div>
                <p className="text-[9px] text-zinc-500 font-bold uppercase mb-1">Manpower Plans (from demand)</p>
                <div className="flex items-center gap-3">
                  <span className="text-lg font-bold text-zinc-800">{adoption.manpower_plans.total_derived_plans}</span>
                  <div className="flex-1 h-2 bg-zinc-100 rounded-full overflow-hidden"><div className="h-full bg-emerald-500 rounded-full" style={{ width: `${adoption.manpower_plans.acceptance_pct}%` }} /></div>
                  <span className="text-[10px] font-bold text-zinc-600">{adoption.manpower_plans.acceptance_pct}% accepted</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
